import { useEffect, useRef } from 'react';

/**
 * Lightweight focus trap + restore for modal dialogs (no new dependencies).
 */
export function useModalFocusTrap(
  active: boolean,
  containerRef: React.RefObject<HTMLElement | null>,
  options?: { returnFocus?: boolean; /** When true, trap Tab only — caller sets initial focus. */ skipInitialFocus?: boolean }
) {
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;

    previousFocus.current = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    if (!container) return;

    const focusables = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.matches(':disabled') && el.tabIndex >= 0 && !el.closest('[aria-hidden="true"]') && el.offsetParent !== null);

    if (!options?.skipInitialFocus) {
      const list = focusables();
      if (list.length > 0) {
        list[0].focus({ preventScroll: true });
      } else {
        container.setAttribute('tabindex', '-1');
        container.focus({ preventScroll: true });
      }
    } else if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === container) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || document.activeElement === container) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      if (options?.returnFocus !== false && previousFocus.current?.focus) {
        previousFocus.current.focus();
      }
    };
  }, [active, containerRef, options?.returnFocus, options?.skipInitialFocus]);
}
